import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { api } from "../../redux/api";

function ExportModal({
  open,
  handleClose,
  selectedItems = [],
  remoteToken,
  urlParams = {},
  remoteUrl,
  connectionId,
  onDeploySuccess,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [comment, setComment] = useState("");
  const [isDeploying, setIsDeploying] = useState(false);
  const [deployError, setDeployError] = useState("");
  const [deploySuccess, setDeploySuccess] = useState("");
  const [blockedFiles, setBlockedFiles] = useState([]);
  const [blockedItems, setBlockedItems] = useState([]);
  const [skippedFiles, setSkippedFiles] = useState([]);
  const [deployWarning, setDeployWarning] = useState("");
  const [overwriteDecisions, setOverwriteDecisions] = useState({});
  const [resolveAllOverwrite, setResolveAllOverwrite] = useState(null);
  const [deploymentCompleted, setDeploymentCompleted] = useState(false);

  const formatErrors = (errors = []) =>
    (errors || [])
      .filter(Boolean)
      .flatMap((error) =>
        String(error)
          .split(",")
          .map((part) => part.trim())
          .filter(Boolean),
      )
      .join(" ");

  useEffect(() => {
    if (open) {
      setComment("");
      setDeployError("");
      setDeploySuccess("");
      setBlockedFiles([]);
      setBlockedItems([]);
      setSkippedFiles([]);
      setDeployWarning("");
      setOverwriteDecisions({});
      setResolveAllOverwrite(null);
      setDeploymentCompleted(false);
    }
  }, [open]);

  const doDeploy = useCallback(
    (forceOverwritePaths = []) => {
      if (
        !remoteToken ||
        !urlParams?.account ||
        !urlParams?.user ||
        !selectedItems?.length
      ) {
        setDeployError("Missing remote token, account, user, or selected items.");
        return;
      }
      setDeployError("");
      setDeploySuccess("");
      setBlockedFiles([]);
      setBlockedItems([]);
      setSkippedFiles([]);
      setDeployWarning("");
      setIsDeploying(true);

      const deployPayload = {
        user: urlParams.user,
        account: urlParams.account,
        comment: comment || "Deploy from portal",
        selectedItems,
        force_overwrite_paths: forceOverwritePaths,
      };

      if (connectionId) {
        deployPayload.connection_id = connectionId;
      }

      // If we know the remote URL (JWT / portal token flow), include it.
      if (remoteUrl) {
        deployPayload.remote_token = remoteToken;
        deployPayload.remote_url = remoteUrl;
      } else {
        // Legacy / opaque remote token flow
        deployPayload.remote_token = remoteToken;
      }

      dispatch(
        api.endpoints.deployToRemoteServer.initiate({
          account: urlParams.account,
          data: deployPayload,
        }),
      )
        .then((result) => {
          setIsDeploying(false);
          if (result?.error) {
            const errorData = result?.error?.data;
            const errorLocked = errorData?.data?.blockedItems || [];
            if (errorLocked.length > 0) {
              setBlockedItems(errorLocked);
            }
            setDeployError(
              errorData?.message ||
                formatErrors(errorData?.data?.errors) ||
                (errorLocked.length > 0
                  ? errorLocked
                      .map((item) => item?.reason || item?.name)
                      .filter(Boolean)
                      .join(" ")
                  : "") ||
                result?.error?.error ||
                "Deploy failed.",
            );
            return;
          }
          const data = result?.data?.data;
          const state = result?.data?.current_state;

          const blocked = data?.blockedFiles || [];
          const locked = data?.blockedItems || [];
          const skipped = data?.skippedFiles || [];

          if (blocked.length > 0 || locked.length > 0) {
            setBlockedFiles(blocked);
            setBlockedItems(locked);
            setSkippedFiles(skipped);
            if (locked.length > 0 && blocked.length === 0) {
              setDeployError(
                locked
                  .map((i) => i.reason || i.name)
                  .join(" ") || "Some items are locked.",
              );
            }
            return;
          }

          if (skipped.length > 0) {
            setSkippedFiles(skipped);
          }

          if (state === "ERROR") {
            setDeployError(
              result?.data?.message ||
                formatErrors(data?.errors) ||
                "Deploy failed.",
            );
            return;
          }

          const summary = data?.summary || {};
          const deployedCount = Number(
            data?.deployedCount ?? summary?.deployedCount ?? 0,
          );
          const skippedCount = Number(
            data?.skippedCount ?? summary?.skippedCount ?? skipped.length ?? 0,
          );
          const failedCount = Number(summary?.failedCount ?? 0);
          const summaryErrors = [
            ...(Array.isArray(data?.errors) ? data.errors : []),
            ...(Array.isArray(summary?.errors) ? summary.errors : []),
          ];

          if (skipped.length > 0) {
            setSkippedFiles(skipped);
            setDeployWarning(
              skipped
                .map((s) => s.warning || s.name)
                .filter(Boolean)
                .join(" "),
            );
          }

          // Some backend responses can return SUCCESS even when nothing was deployed.
          // Treat that as failure if there are errors/failed items or deployedCount is zero.
          if (
            failedCount > 0 ||
            summaryErrors.length > 0 ||
            (deployedCount === 0 && skippedCount === 0)
          ) {
            setDeployError(
              formatErrors(summaryErrors) ||
                result?.data?.message ||
                "Deployment did not complete for the selected item(s).",
            );
            if (deployedCount > 0 || skippedCount > 0) {
              onDeploySuccess?.();
            }
            return;
          }

          if (deployedCount === 0 && skippedCount > 0) {
            setDeploymentCompleted(true);
            onDeploySuccess?.();
            return;
          }

          if (deployedCount === 0) {
            setDeployError(
              result?.data?.message ||
                "Deployment did not complete for the selected item(s).",
            );
            return;
          }

          const successParts = [];
          if (deployedCount > 0) {
            successParts.push(`Deployed ${deployedCount} item(s) successfully.`);
          }
          if (skippedCount > 0) {
            successParts.push(
              `${skippedCount} identical item(s) skipped (no changes needed).`,
            );
          }
          setDeploySuccess(successParts.join(" "));
          setDeploymentCompleted(true);
          onDeploySuccess?.();
        })
        .catch(() => {
          setIsDeploying(false);
          setDeployError("Deploy failed.");
        });
    },
    [
      dispatch,
      remoteToken,
      remoteUrl,
      connectionId,
      urlParams?.account,
      urlParams?.user,
      selectedItems,
      comment,
      onDeploySuccess,
    ],
  );

  const handleDeploy = () => {
    if (blockedFiles.length > 0) {
      const paths = [];
      for (const bf of blockedFiles) {
        const path = bf.path;
        const decision =
          resolveAllOverwrite !== null
            ? resolveAllOverwrite
            : overwriteDecisions[path];
        if (decision === true) paths.push(path);
      }
      if (paths.length > 0 || resolveAllOverwrite === false) {
        doDeploy(paths);
        setBlockedFiles([]);
        setOverwriteDecisions({});
        setResolveAllOverwrite(null);
      }
    } else {
      doDeploy();
    }
  };

  const handleOverwriteDecision = (path, overwrite) => {
    setOverwriteDecisions((prev) => ({ ...prev, [path]: overwrite }));
  };

  const handleYesToAll = () => {
    setResolveAllOverwrite(true);
    const paths = blockedFiles.map((bf) => bf.path).filter(Boolean);
    doDeploy(paths);
    setBlockedFiles([]);
    setOverwriteDecisions({});
    setResolveAllOverwrite(null);
  };

  const handleNoToAll = () => {
    setResolveAllOverwrite(false);
    doDeploy([]);
    setBlockedFiles([]);
    setOverwriteDecisions({});
    setResolveAllOverwrite(null);
  };

  const canClose = !isDeploying;

  return (
    <Dialog open={open} onClose={canClose ? handleClose : undefined}>
      <DialogTitle className="dialog-header">
        <div className="flex space-between align-center">
          <div className="title">
            {t("DEPLOY_TO_SERVER") || "Deploy to Server"}
          </div>
          <IconButton
            onClick={canClose ? handleClose : undefined}
            disabled={!canClose}
            sx={{ borderRadius: "5px" }}>
            <CloseIcon
              sx={{
                height: "28px",
                width: "28px",
                color: "#777777",
              }}
            />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent className="dialog-content" dividers>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>{t("WARNING") || "Warning"}:</strong> This action will deploy
          the selected items from your current server to the remote server. This
          operation cannot be undone.
        </Typography>
        <TextField
          label={t("LBL_NEW_COMMENT") || "Comment"}
          multiline
          rows={3}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          fullWidth
          margin="normal"
          placeholder={
            t("LBL_ENTER_DEPLOYMENT_COMMENT") || "Enter deployment comment"
          }
        />

        {deployError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {deployError}
          </Alert>
        )}
        {deploySuccess && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {deploySuccess}
          </Alert>
        )}
        {deployWarning && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {deployWarning}
          </Alert>
        )}

        {blockedItems.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Some items are locked on the remote server and cannot be deployed:
            </Typography>
            <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
              {blockedItems.map((item, i) => (
                <li key={i}>{item.reason || item.name}</li>
              ))}
            </ul>
          </Alert>
        )}

        {skippedFiles.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Some files were skipped because they are identical on the remote server:
            </Typography>
            <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
              {skippedFiles.map((item, i) => (
                <li key={i}>{item.message || item.name || item.path}</li>
              ))}
            </ul>
          </Alert>
        )}

        {blockedFiles.length > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Server has newer versions for some files. Overwrite?
            </Typography>
            {blockedFiles.map((bf) => (
              <div
                key={bf.path}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 8,
                }}>
                <Typography variant="caption">{bf.name}:</Typography>
                <Button
                  size="small"
                  variant={overwriteDecisions[bf.path] === true ? "contained" : "outlined"}
                  onClick={() => handleOverwriteDecision(bf.path, true)}>
                  Yes
                </Button>
                <Button
                  size="small"
                  variant={overwriteDecisions[bf.path] === false ? "contained" : "outlined"}
                  onClick={() => handleOverwriteDecision(bf.path, false)}>
                  No
                </Button>
              </div>
            ))}
          </Alert>
        )}
      </DialogContent>
      <DialogActions className="dialog-actions">
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={isDeploying}>
          {t("CANCEL") || "Cancel"}
        </Button>
        {blockedFiles.length > 0 ? (
          <>
            <Button
              variant="outlined"
              onClick={handleNoToAll}
              disabled={isDeploying}>
              No to All
            </Button>
            <Button
              variant="outlined"
              onClick={handleYesToAll}
              disabled={isDeploying}>
              Yes to All
            </Button>
            <Button
              onClick={handleDeploy}
              disabled={isDeploying}
              variant="outlined"
              color="primary"
              startIcon={isDeploying ? <CircularProgress size={16} /> : null}>
              {isDeploying ? "Deploying…" : "Deploy with selected"}
            </Button>
          </>
        ) : (
          <Button
            onClick={handleDeploy}
            disabled={isDeploying || !remoteToken || deploymentCompleted}
            variant="outlined"
            color="primary"
            startIcon={isDeploying ? <CircularProgress size={16} /> : null}>
            {isDeploying
              ? `${t("DEPLOYING") || "Deploying"}...`
              : t("DEPLOY_TO_SERVER") || "Deploy to Server"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ExportModal;
